import { useCallback, useEffect, useReducer, useRef } from "react";
import {
  ChatDocumentFailure,
  ChatDocumentPhase,
  ChatDocumentRequestError,
  ChatDocumentStatus,
  deleteChatAttachment,
  fetchChatDocumentStatus,
  uploadChatDocument,
} from "@shared/api/chatApi";
import { ApiError } from "@shared/type/ApiType";
import { sanitizeDisplayName } from "./useChatAttachment";

/**
 * 문서(PDF/PNG/JPEG) 첨부 상태 머신(IDP plan §Task 21) — 표(xlsx/csv) 첨부 훅과 완전 분리.
 * 202 수락 → 본문 폴링 → READY|FAILED 수명주기를 세대 fence로 다룬다: 교체/삭제/클리어 이후
 * 늦게 도착한 구 응답은 상태를 절대 갱신하지 못한다. 파일명은 로컬 표시 전용이며 서버로
 * 어떤 형태로도 전송되지 않는다.
 */

export type ChatDocumentKind = "pdf" | "png" | "jpg" | "jpeg";

/** 확장자 판정 — 미지원은 null(수락 여부·바이트 검증은 전적으로 서버 소관). */
export function documentKindOf(fileName: string): ChatDocumentKind | null {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".pdf")) return "pdf";
  if (lower.endsWith(".png")) return "png";
  if (lower.endsWith(".jpg")) return "jpg";
  if (lower.endsWith(".jpeg")) return "jpeg";
  return null;
}

export type ChatDocumentAttachmentState =
  | { status: "IDLE" }
  | {
      status: "UPLOADING";
      displayName: string;
      /** 업로드 거절 시 되돌아갈 이전 문서(리뷰 P1-7) — 서버에 살아 있는 문서를 UI에서 잃지 않게. */
      previous?: ChatDocumentResumableState;
    }
  | {
      status: "PROCESSING";
      attachmentId: string;
      displayName: string;
      phase: ChatDocumentPhase;
      processedPages: number;
      totalPages?: number;
      queueDeadline?: string;
      processingDeadline?: string;
    }
  | {
      status: "READY";
      attachmentId: string;
      displayName: string;
      documentQuality: "COMPLETE" | "REVIEW_REQUIRED";
      lineCompleteness: "VERIFIED_LOCAL" | "UNKNOWN_RASTER_LINE_COMPLETENESS";
      totalPages: number;
      expiresAt: string;
      hardExpiresAt: string;
    }
  | { status: "FAILED"; displayName: string; error: ChatDocumentFailure }
  | {
      status: "REMOVING";
      attachmentId: string;
      displayName: string;
      /** 제거 실패 시 되돌아갈 상태(리뷰 F1) — REMOVING에 갇히지 않게 한다. */
      previous: ChatDocumentResumableState;
    };

type ChatDocumentResumableState = Extract<
  ChatDocumentAttachmentState,
  { status: "PROCESSING" | "READY" }
>;

export type ChatDocumentAttachmentAction =
  | { type: "UPLOAD_START"; displayName: string; previous?: ChatDocumentResumableState }
  | { type: "UPLOAD_ACCEPTED"; attachmentId: string; queueDeadline: string }
  | { type: "UPLOAD_REJECTED"; error: ChatDocumentFailure }
  | { type: "STATUS"; statusBody: ChatDocumentStatus }
  | { type: "GONE" }
  | { type: "REMOVE_START" }
  | { type: "REMOVE_SUCCESS" }
  | { type: "REMOVE_FAILURE" }
  | { type: "POLL_ABANDONED"; error: ChatDocumentFailure }
  | { type: "CLEAR_LOCAL" };

export function documentAttachmentReducer(
  state: ChatDocumentAttachmentState,
  action: ChatDocumentAttachmentAction
): ChatDocumentAttachmentState {
  switch (action.type) {
    case "UPLOAD_START":
      return { status: "UPLOADING", displayName: action.displayName, previous: action.previous };
    case "UPLOAD_ACCEPTED":
      return state.status === "UPLOADING"
        ? {
            status: "PROCESSING",
            attachmentId: action.attachmentId,
            displayName: state.displayName,
            phase: "QUEUED",
            processedPages: 0,
            queueDeadline: action.queueDeadline,
          }
        : state;
    case "UPLOAD_REJECTED":
      if (state.status !== "UPLOADING") {
        return state;
      }
      // 이전 문서가 있었으면 복원(서버가 교체를 이미 수행한 경우는 다음 폴링/질문의 410이 정리한다).
      return state.previous ?? { status: "FAILED", displayName: state.displayName, error: action.error };
    case "STATUS": {
      if (state.status !== "PROCESSING") {
        return state; // 폴링 대상이 아닌 상태로의 늦은 응답 — 최신 상태 보존
      }
      const body = action.statusBody;
      if (!body || typeof body !== "object") {
        return state; // 빈/비JSON 200 본문 — 렌더 중 크래시 대신 다음 폴링을 기다린다(리뷰 F2)
      }
      if (body.state === "PROCESSING") {
        return {
          status: "PROCESSING",
          attachmentId: state.attachmentId,
          displayName: state.displayName,
          phase: body.phase,
          processedPages: body.processedPages,
          totalPages: body.totalPages ?? undefined,
          queueDeadline: body.queueDeadline ?? undefined,
          processingDeadline: body.processingDeadline ?? undefined,
        };
      }
      if (body.state === "READY") {
        return {
          status: "READY",
          attachmentId: state.attachmentId,
          displayName: state.displayName,
          documentQuality: body.documentQuality,
          lineCompleteness: body.lineCompleteness,
          totalPages: body.totalPages,
          expiresAt: body.expiresAt,
          hardExpiresAt: body.hardExpiresAt,
        };
      }
      // FAILED — 서버 error 본문이 없거나 예상 밖 상태값이면 폴백 사유로 표시한다(칩 크래시 방지, 2026-09-03).
      const failure: ChatDocumentFailure =
        body.state === "FAILED" && body.error
          ? body.error
          : {
              code: String((body as { state?: unknown; code?: unknown }).code
                ?? (body as { state?: unknown }).state ?? "UNKNOWN_STATUS"),
              // 서버가 봉투형 오류(200 + {status,message})를 준 경우 그 message를 그대로 보여 원인을 드러낸다.
              messageKo: `문서 상태를 해석하지 못했습니다: ${String(
                (body as { message?: unknown; messageKo?: unknown }).messageKo
                  ?? (body as { message?: unknown }).message
                  ?? (body as { state?: unknown }).state
                  ?? "응답 없음"
              )}`,
              recoverable: true,
            };
      return { status: "FAILED", displayName: state.displayName, error: failure };
    }
    case "GONE":
    case "REMOVE_SUCCESS":
    case "CLEAR_LOCAL":
      return { status: "IDLE" };
    case "REMOVE_START":
      return state.status === "READY" || state.status === "PROCESSING"
        ? {
            status: "REMOVING",
            attachmentId: state.attachmentId,
            displayName: state.displayName,
            previous: state,
          }
        : state;
    case "REMOVE_FAILURE":
      // 서버 제거 미확인 — 성공을 주장하지 않고 제거 전 상태로 되돌린다(칩 X·전송·첨부가 다시 살아나
      // 재시도할 수 있다, 리뷰 F1). 서버 첨부는 TTL이 정리한다.
      return state.status === "REMOVING" ? state.previous : state;
    case "POLL_ABANDONED":
      return state.status === "PROCESSING"
        ? { status: "FAILED", displayName: state.displayName, error: action.error }
        : state;
    default: {
      const exhaustive: never = action;
      return exhaustive;
    }
  }
}

const NETWORK_RETRY_MS = 5_000;
/** 연속 폴링 실패 상한(리뷰 F3) — 5초 간격 12회 = 1분. 그 뒤는 실패로 종결해 무한 폴링을 막는다. */
const MAX_CONSECUTIVE_POLL_FAILURES = 12;
const POLL_ABANDONED_FAILURE: ChatDocumentFailure = {
  code: "STATUS_UNAVAILABLE",
  messageKo: "문서 상태 확인에 반복 실패했습니다. 문서를 다시 업로드해 주세요.",
  recoverable: true,
};

export function useChatDocumentAttachment() {
  const [state, dispatch] = useReducer(documentAttachmentReducer, { status: "IDLE" });
  /** 로컬 세대 — 교체/삭제/클리어마다 증가. 늦은 업로드/폴 응답의 상태 커밋을 차단한다. */
  const generationRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const pollFailuresRef = useRef(0);
  const stateRef = useRef(state);
  stateRef.current = state;

  const stopPolling = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  useEffect(
    () => () => {
      // 언마운트 — 타이머/요청 정리(서버 취소 주장은 하지 않는다).
      stopPolling();
      generationRef.current += 1;
    },
    [stopPolling]
  );

  const schedulePoll = useCallback(
    (attachmentId: string, generation: number, delayMs: number) => {
      if (generationRef.current !== generation) {
        return;
      }
      timerRef.current = setTimeout(async () => {
        if (generationRef.current !== generation) {
          return;
        }
        const abort = new AbortController();
        abortRef.current = abort;
        try {
          const statusBody = await fetchChatDocumentStatus(attachmentId, abort.signal);
          if (generationRef.current !== generation) {
            return; // 교체/삭제 이후 도착한 구 응답 — 커밋 금지
          }
          pollFailuresRef.current = 0;
          dispatch({ type: "STATUS", statusBody });
          if (statusBody && statusBody.state === "PROCESSING") {
            schedulePoll(attachmentId, generation, statusBody.pollAfterMs);
          }
        } catch (error) {
          if (generationRef.current !== generation) {
            return;
          }
          if (error instanceof ChatDocumentRequestError && error.httpStatus === 410) {
            dispatch({ type: "GONE" }); // 만료/제거 — 권위 있는 정리 신호, 재업로드로 복구
            return;
          }
          if (
            error instanceof ChatDocumentRequestError
            && (error.httpStatus === 401 || error.httpStatus === 403 || error.httpStatus === 404)
          ) {
            // 인증/접근/경로 오류는 재시도로 풀리지 않는다 — 무한 폴링 대신 정리(리뷰 F3).
            dispatch({ type: "GONE" });
            return;
          }
          pollFailuresRef.current += 1;
          if (pollFailuresRef.current >= MAX_CONSECUTIVE_POLL_FAILURES) {
            pollFailuresRef.current = 0;
            dispatch({ type: "POLL_ABANDONED", error: POLL_ABANDONED_FAILURE });
            return;
          }
          // 일시 네트워크 오류 — 성공/실패를 단정하지 않고 재시도(서버 기한이 진실).
          schedulePoll(attachmentId, generation, NETWORK_RETRY_MS);
        }
      }, delayMs);
    },
    []
  );

  const selectDocument = useCallback(
    async (file: File): Promise<{ ok: boolean; message?: string }> => {
      const current = stateRef.current;
      if (current.status === "UPLOADING" || current.status === "REMOVING") {
        return { ok: false, message: "문서 처리 중입니다. 잠시 후 다시 시도해 주세요." };
      }
      const kind = documentKindOf(file.name);
      if (!kind) {
        return { ok: false, message: "PDF, PNG, JPEG 문서만 업로드할 수 있습니다." };
      }
      const displayName = sanitizeDisplayName(file.name) || `document.${kind}`;
      const previous =
        current.status === "READY" || current.status === "PROCESSING" ? current : undefined;
      stopPolling();
      const myGeneration = ++generationRef.current;
      const abort = new AbortController();
      abortRef.current = abort;
      dispatch({ type: "UPLOAD_START", displayName, previous });
      try {
        const accepted = await uploadChatDocument(file, abort.signal);
        if (generationRef.current !== myGeneration) {
          return { ok: false }; // 교체 경쟁 — 구 업로드 응답은 커밋 금지
        }
        dispatch({
          type: "UPLOAD_ACCEPTED",
          attachmentId: accepted.attachmentId,
          queueDeadline: accepted.queueDeadline,
        });
        schedulePoll(accepted.attachmentId, myGeneration, accepted.pollAfterMs);
        return { ok: true };
      } catch (error) {
        if (generationRef.current !== myGeneration) {
          return { ok: false };
        }
        const failure: ChatDocumentFailure =
          error instanceof ChatDocumentRequestError
            ? { code: error.code, messageKo: error.message, recoverable: error.recoverable }
            : {
                code: "NETWORK",
                messageKo: "문서 업로드 중 오류가 발생했습니다. 다시 시도해 주세요.",
                recoverable: true,
              };
        dispatch({ type: "UPLOAD_REJECTED", error: failure });
        if (previous?.status === "PROCESSING") {
          schedulePoll(previous.attachmentId, myGeneration, NETWORK_RETRY_MS); // 이전 문서 폴링 재개
        }
        return { ok: false, message: failure.messageKo };
      } finally {
        if (abortRef.current === abort) {
          abortRef.current = null;
        }
      }
    },
    [schedulePoll, stopPolling]
  );

  const remove = useCallback(async (): Promise<{ ok: boolean; message?: string }> => {
    const current = stateRef.current;
    if (
      current.status !== "READY"
      && current.status !== "PROCESSING"
      && current.status !== "REMOVING"
    ) {
      return { ok: false };
    }
    generationRef.current += 1; // 진행 중 폴링의 어떤 응답도 이후 커밋 불가
    stopPolling();
    dispatch({ type: "REMOVE_START" });
    try {
      await deleteChatAttachment(current.attachmentId);
      dispatch({ type: "REMOVE_SUCCESS" });
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError && error.code === 410) {
        dispatch({ type: "REMOVE_SUCCESS" }); // 이미 만료/제거 — 권위 있는 정리 신호
        return { ok: true };
      }
      dispatch({ type: "REMOVE_FAILURE" });
      const resumed = current.status === "REMOVING" ? current.previous : current;
      if (resumed.status === "PROCESSING") {
        // 제거 전이 처리 중이었다면 폴링을 다시 잇는다(세대는 REMOVE_START에서 이미 올렸다).
        schedulePoll(resumed.attachmentId, generationRef.current, NETWORK_RETRY_MS);
      }
      return {
        ok: false,
        message: "서버에서 문서 제거를 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.",
      };
    }
  }, [schedulePoll, stopPolling]);

  /** 로컬 상태만 소거 — 410 수신/채팅 클리어용(서버 정리 주장 금지). */
  const clearLocal = useCallback(() => {
    generationRef.current += 1;
    stopPolling();
    dispatch({ type: "CLEAR_LOCAL" });
  }, [stopPolling]);

  return {
    state,
    generation: () => generationRef.current,
    selectDocument,
    remove,
    clearLocal,
  };
}

export type ChatDocumentAttachmentHook = ReturnType<typeof useChatDocumentAttachment>;
